/**
 * Compose an NpiSchedule + NpiSpec library into a time-varying β scaler.
 *
 * Multiple overlapping NPIs compose multiplicatively:
 *   β(t) = β_base · ∏_{active NPI} betaScale_i
 *
 * v0.1 ignores timeToEffectDays + fatigueHalfLifeDays — both are
 * carried in the spec for v0.2+ behavioural-layer consumers. Apply
 * the central betaScale value across the activation window.
 */

import type { BetaScaler, NpiSchedule, NpiSpec } from './types.js';

export function composeBetaScaler(
  schedule: readonly NpiSchedule[],
  library: Record<string, NpiSpec>,
): BetaScaler {
  // Pre-resolve specs once.
  const resolved: Array<{ s: NpiSchedule; spec: NpiSpec }> = [];
  for (const s of schedule) {
    const spec = library[s.npiId];
    if (!spec) throw new Error(`composeBetaScaler: NPI '${s.npiId}' not in library`);
    resolved.push({ s, spec });
  }
  return (t: number): number => {
    let scale = 1;
    for (const { s, spec } of resolved) {
      if (t >= s.startDay && t <= s.endDay) scale *= spec.betaScale;
    }
    return scale;
  };
}

/** Compose multiple BetaScalers multiplicatively. */
export function multiplyScalers(...scalers: BetaScaler[]): BetaScaler {
  return (t: number): number => {
    let s = 1;
    for (const f of scalers) s *= f(t);
    return s;
  };
}

/** A constant scaler — useful as a no-op default. */
export const identityScaler: BetaScaler = () => 1;
